import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { Loader2, LogOut, Users, RefreshCw, Download } from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from "recharts";
import { Logo } from "../components/Logo";
import { ORG_RESULTS } from "../data/orgAssessment";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "cc_elevate_admin_token";

const BAND_LABEL = { reattiva: "Reattiva", transizione: "Transizione", orchestrata: "Orchestrata" };

export default function Admin() {
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY) || "");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [orgLeads, setOrgLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [orgSelected, setOrgSelected] = useState(null);
  const [orgSearch, setOrgSearch] = useState("");
  const [orgBand, setOrgBand] = useState("all");

  const loadData = useCallback(async (tk) => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${tk}` };
      const [o, s] = await Promise.all([
        axios.get(`${API}/admin/org-leads`, { headers }),
        axios.get(`${API}/admin/stats`, { headers }),
      ]);
      setOrgLeads(o.data.leads);
      setStats(s.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
        setError("Sessione scaduta, effettua di nuovo l'accesso.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (token) loadData(token);
  }, [token, loadData]);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
    } catch (err) {
      setError(err?.response?.data?.detail || "Password non corretta");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setOrgLeads([]);
    setStats(null);
  };

  const download = (rows, name) => {
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const csv = rows.map((r) => r.map(esc).join(",")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${name}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportCSV = () => {
    const qCols = Array.from({ length: 20 }, (_, i) => `Q${i + 1}`);
    const headers = ["Data", "Nome e Cognome", "Ruolo", "Azienda", "Email", "Dipendenti", "Telefono", "Punteggio", "Fascia", ...qCols, "Email inviata", "Newsletter"];
    const rows = orgLeads.map((l) => [
      l.created_at, l.full_name, l.role, l.company, l.email, l.employees, l.phone, l.score, BAND_LABEL[l.band],
      ...Array.from({ length: 20 }, (_, i) => l.answers[`q${i + 1}`]),
      l.email_sent ? "Si" : "No",
      l.marketing_accepted ? "Si" : "No",
    ]);
    download([headers, ...rows], "cc-elevate-organizzativo");
  };

  const filteredOrgLeads = useMemo(() => {
    const q = orgSearch.trim().toLowerCase();
    return orgLeads.filter((l) => {
      if (orgBand !== "all" && l.band !== orgBand) return false;
      if (!q) return true;
      return (
        (l.company || "").toLowerCase().includes(q) ||
        (l.email || "").toLowerCase().includes(q) ||
        (l.full_name || "").toLowerCase().includes(q)
      );
    });
  }, [orgLeads, orgSearch, orgBand]);

  const fmtDate = (iso) => {
    try {
      return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch {
      return iso;
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-black text-white flex flex-col items-center justify-center px-4">
        <Logo className="h-24 mb-10" />
        <div className="relative gold-frame bg-[#0A0A0A] border border-white/10 px-8 py-10 w-full max-w-sm">
          <p className="text-xs font-bold tracking-[0.25em] text-gold uppercase text-center">Area Riservata</p>
          <h1 className="mt-4 font-serif text-3xl text-white text-center">Dashboard Admin</h1>
          <form onSubmit={login} className="mt-8 space-y-4">
            <input
              type="password"
              data-testid="admin-password-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              className="w-full bg-[#111] border border-white/15 px-4 py-3 text-sm text-white placeholder:text-white/30 outline-none focus:border-gold transition-colors duration-300"
            />
            {error && <p className="text-xs text-red-400" data-testid="admin-error">{error}</p>}
            <button
              type="submit"
              disabled={loading}
              data-testid="admin-login-btn"
              className="w-full inline-flex items-center justify-center gap-2 px-8 py-3.5 text-sm font-bold tracking-[0.15em] uppercase bg-gold text-black hover:bg-gold-hover transition-colors duration-300 disabled:opacity-50"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Accedi"}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white px-4 sm:px-8 py-8">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between flex-wrap gap-4 border-b border-white/10 pb-6">
          <div className="flex items-center gap-4">
            <Logo className="h-14" />
            <div>
              <p className="text-[11px] font-bold tracking-[0.25em] text-gold uppercase">Assessment Organizzativo</p>
              <h1 className="font-serif text-2xl text-white">Dashboard Lead</h1>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={exportCSV}
              disabled={orgLeads.length === 0}
              data-testid="export-csv-btn"
              className="inline-flex items-center gap-2 px-4 py-2.5 text-xs font-bold tracking-[0.1em] uppercase text-black bg-gold hover:bg-gold-hover transition-colors duration-300 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Download className="h-3.5 w-3.5" /> Esporta CSV
            </button>
            <button
              onClick={() => loadData(token)}
              data-testid="refresh-btn"
              className="inline-flex items-center gap-2 px-4 py-2.5 text-xs font-bold tracking-[0.1em] uppercase text-white/70 border border-white/15 hover:border-gold hover:text-gold transition-colors duration-300"
            >
              <RefreshCw className="h-3.5 w-3.5" /> Aggiorna
            </button>
            <button
              onClick={logout}
              data-testid="logout-btn"
              className="inline-flex items-center gap-2 px-4 py-2.5 text-xs font-bold tracking-[0.1em] uppercase text-white/70 border border-white/15 hover:border-red-400 hover:text-red-400 transition-colors duration-300"
            >
              <LogOut className="h-3.5 w-3.5" /> Esci
            </button>
          </div>
        </div>

        {/* Stats */}
        {stats && (
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-4">
            <StatCard label="Assessment Totali" value={stats.org_total} icon />
            <StatCard label="Reattiva" value={stats.org_by_band.reattiva} />
            <StatCard label="Transizione" value={stats.org_by_band.transizione} />
            <StatCard label="Orchestrata" value={stats.org_by_band.orchestrata} />
          </div>
        )}

        {/* Chart */}
        {stats && stats.org_total > 0 && (
          <ChartCard
            title="Distribuzione Salute Organizzativa"
            data={[
              { name: "Reattiva", value: stats.org_by_band.reattiva },
              { name: "Transizione", value: stats.org_by_band.transizione },
              { name: "Orchestrata", value: stats.org_by_band.orchestrata },
            ]}
            colors={["#8B3A2F", "#C9922B", "#D4AF37"]}
            testId="org-chart"
          />
        )}

        {/* Filters */}
        <div className="mt-8 flex flex-col sm:flex-row gap-3 sm:items-center">
          <input
            value={orgSearch}
            onChange={(e) => setOrgSearch(e.target.value)}
            data-testid="org-search-input"
            placeholder="Cerca per azienda, email o nome..."
            className="flex-1 bg-[#111] border border-white/15 px-4 py-2.5 text-sm text-white placeholder:text-white/30 outline-none focus:border-gold transition-colors duration-300"
          />
          <select
            value={orgBand}
            onChange={(e) => setOrgBand(e.target.value)}
            data-testid="org-band-filter"
            className="bg-[#111] border border-white/15 px-4 py-2.5 text-sm text-white outline-none focus:border-gold transition-colors duration-300 cursor-pointer sm:w-56"
          >
            <option value="all" className="bg-[#111]">Tutte le fasce</option>
            <option value="reattiva" className="bg-[#111]">Reattiva</option>
            <option value="transizione" className="bg-[#111]">Transizione</option>
            <option value="orchestrata" className="bg-[#111]">Orchestrata</option>
          </select>
          {(orgSearch || orgBand !== "all") && (
            <button
              onClick={() => { setOrgSearch(""); setOrgBand("all"); }}
              data-testid="org-clear-filters"
              className="px-4 py-2.5 text-xs font-bold tracking-[0.1em] uppercase text-white/60 border border-white/15 hover:border-gold hover:text-gold transition-colors duration-300"
            >
              Azzera
            </button>
          )}
        </div>
        <p className="mt-2 text-xs text-white/40" data-testid="org-filter-count">
          {filteredOrgLeads.length} di {orgLeads.length} assessment
        </p>

        {/* Table */}
        <div className="mt-3 border border-white/10 overflow-x-auto">
          <table className="w-full text-sm" data-testid="org-leads-table">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-[0.12em] text-gold/80 border-b border-white/10">
                <th className="px-4 py-3 font-bold">Data</th>
                <th className="px-4 py-3 font-bold">Nome</th>
                <th className="px-4 py-3 font-bold">Azienda</th>
                <th className="px-4 py-3 font-bold">Ruolo</th>
                <th className="px-4 py-3 font-bold">Email</th>
                <th className="px-4 py-3 font-bold">Dip.</th>
                <th className="px-4 py-3 font-bold">Punteggio</th>
                <th className="px-4 py-3 font-bold">Fascia</th>
                <th className="px-4 py-3 font-bold">Email inviata</th>
                <th className="px-4 py-3 font-bold"></th>
              </tr>
            </thead>
            <tbody>
              {loading && orgLeads.length === 0 && (
                <tr><td colSpan={10} className="px-4 py-10 text-center text-white/40">Caricamento...</td></tr>
              )}
              {!loading && orgLeads.length === 0 && (
                <tr><td colSpan={10} className="px-4 py-10 text-center text-white/40" data-testid="no-org-leads">Nessun assessment raccolto finora.</td></tr>
              )}
              {!loading && orgLeads.length > 0 && filteredOrgLeads.length === 0 && (
                <tr><td colSpan={10} className="px-4 py-10 text-center text-white/40" data-testid="no-org-results">Nessun risultato per i filtri selezionati.</td></tr>
              )}
              {filteredOrgLeads.map((lead) => (
                <tr key={lead.lead_id} className="border-b border-white/5 hover:bg-white/[0.03] transition-colors">
                  <td className="px-4 py-3 text-white/60 whitespace-nowrap">{fmtDate(lead.created_at)}</td>
                  <td className="px-4 py-3 text-white whitespace-nowrap">{lead.full_name}</td>
                  <td className="px-4 py-3 text-white/80">{lead.company}</td>
                  <td className="px-4 py-3 text-white/60">{lead.role}</td>
                  <td className="px-4 py-3 text-white/80">{lead.email}</td>
                  <td className="px-4 py-3 text-white/60 whitespace-nowrap">{lead.employees}</td>
                  <td className="px-4 py-3 text-gold font-bold">{lead.score}</td>
                  <td className="px-4 py-3">
                    <span className="inline-block px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider border border-gold/50 text-gold whitespace-nowrap">
                      {BAND_LABEL[lead.band]}
                    </span>
                  </td>
                  <td className="px-4 py-3">{lead.email_sent ? <span className="text-gold">Sì</span> : <span className="text-white/40">No</span>}</td>
                  <td className="px-4 py-3">
                    <button onClick={() => setOrgSelected(lead)} data-testid={`view-org-lead-${lead.lead_id}`} className="text-xs font-bold uppercase tracking-wider text-gold hover:text-gold-hover">
                      Risposte
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {orgSelected && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-50" onClick={() => setOrgSelected(null)}>
          <div
            className="relative gold-frame bg-[#0A0A0A] border border-white/10 max-w-lg w-full max-h-[85vh] overflow-y-auto p-8"
            onClick={(e) => e.stopPropagation()}
            data-testid="org-lead-detail-modal"
          >
            <p className="text-[11px] font-bold tracking-[0.2em] uppercase text-gold">Dettaglio risposte (scala 1-5)</p>
            <h2 className="mt-2 font-serif text-2xl text-white">{orgSelected.full_name}</h2>
            <p className="text-white/60 text-sm">{orgSelected.company} &middot; {orgSelected.role}</p>
            <p className="mt-1 text-gold text-sm">
              Punteggio: {orgSelected.score}/100 &middot; {ORG_RESULTS[orgSelected.band].title}
            </p>
            <div className="mt-6 space-y-3">
              {Array.from({ length: 20 }, (_, i) => i + 1).map((n) => (
                <div key={n} className="flex items-center justify-between border-b border-white/10 pb-2">
                  <span className="text-white/70 text-sm">Affermazione {n}</span>
                  <span className="text-gold font-bold text-sm">{orgSelected.answers[`q${n}`]}</span>
                </div>
              ))}
            </div>
            <button
              onClick={() => setOrgSelected(null)}
              className="mt-7 w-full px-6 py-3 text-xs font-bold tracking-[0.15em] uppercase bg-gold text-black hover:bg-gold-hover transition-colors duration-300"
            >
              Chiudi
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const ChartCard = ({ title, data, testId, colors = ["#8a6d1f", "#B5911F", "#D4AF37"] }) => (
  <div className="mt-4 border border-white/10 bg-[#0A0A0A] px-5 py-6">
    <p className="text-[11px] font-bold tracking-[0.2em] uppercase text-gold mb-2">{title}</p>
    <div className="h-64 w-full" data-testid={testId}>
      <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={240}>
        <PieChart>
          <Pie data={data} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} innerRadius={45} paddingAngle={2} stroke="#000000">
            {colors.map((c, i) => (
              <Cell key={i} fill={c} />
            ))}
          </Pie>
          <Tooltip contentStyle={{ background: "#111", border: "1px solid #333", color: "#fff", fontSize: 13 }} itemStyle={{ color: "#D4AF37" }} />
          <Legend wrapperStyle={{ fontSize: 12, textTransform: "uppercase", letterSpacing: "0.1em" }} />
        </PieChart>
      </ResponsiveContainer>
    </div>
  </div>
);

const StatCard = ({ label, value, icon }) => (
  <div className="border border-white/10 bg-[#0A0A0A] px-5 py-5">
    <div className="flex items-center justify-between">
      <p className="text-[11px] font-bold tracking-[0.15em] uppercase text-white/50">{label}</p>
      {icon && <Users className="h-4 w-4 text-gold" />}
    </div>
    <p className="mt-2 font-serif text-3xl text-gold">{value}</p>
  </div>
);
